import type { RemoteHistorySummary } from '../account/historyApi'
import type { HistorySummaryEventDetail } from '../events/historySummary'
import { translate } from '../i18n'
import { applyHistorySummaryUpdate } from '../state/historySummaryStore'
import { applyHistoryTimeBlocksUpdate } from '../state/historyTimeBlocksStore'
import {
   collectTimeBlockRanges,
   getFastEntryDurationMs,
   type FastEntry,
} from '../utils/fastEntry'
import { formatFatGrams, getCumulativeFatBurned } from '../utils/fatBurn'
import { getDaysCutoff, getMonthCutoff } from '../utils/time'

const DAILY_KCAL_DEFAULT = 2000
const REMOTE_SUMMARY_CACHE_TTL_MS = 60_000

const HISTORY_SUMMARY_PERIOD_CONFIG = {
   week: {
      key: 'week',
      labelKey: 'Past Week',
      shortLabelKey: 'Week',
      type: 'days',
      amount: 7,
      descriptionKey: 'Combined fat burned across your fasts this week.',
      emptyMessageKey: 'No completed fasts during this week.',
   },
   month: {
      key: 'month',
      labelKey: 'Past Month',
      shortLabelKey: 'Month',
      type: 'months',
      amount: 1,
      descriptionKey: 'Combined fat burned across your fasts this month.',
      emptyMessageKey: 'No completed fasts during this month.',
   },
   year: {
      key: 'year',
      labelKey: 'Past Year',
      shortLabelKey: 'Year',
      type: 'months',
      amount: 12,
      descriptionKey: 'Combined fat burned across your fasts this year.',
      emptyMessageKey: 'No completed fasts during this year.',
   },
} as const

type HistorySummaryPeriodKey = keyof typeof HISTORY_SUMMARY_PERIOD_CONFIG

type HistorySummaryData = {
   period: HistorySummaryPeriodKey
   totalGrams: number
   fastCount: number
}

type HistorySummaryControllerDeps = {
   getFastHistory: () => FastEntry[]
   getCurrentFastSnapshot: () => FastEntry | null
   canFetchRemote: () => boolean
   fetchRemoteSummary: (
      period: HistorySummaryPeriodKey,
   ) => Promise<RemoteHistorySummary | null>
}

/**
 * Type guard for whether a string is a known history-summary period key.
 *
 * @param value - The candidate key.
 * @returns True when it is a valid period key.
 */
function isHistorySummaryPeriod(value: string): value is HistorySummaryPeriodKey {
   return Object.prototype.hasOwnProperty.call(HISTORY_SUMMARY_PERIOD_CONFIG, value)
}

/**
 * Resolves a period key to its config with translated labels.
 *
 * @param key - The period key.
 * @returns The resolved period descriptor.
 */
function getHistorySummaryPeriod(key: HistorySummaryPeriodKey) {
   const config = HISTORY_SUMMARY_PERIOD_CONFIG[key]
   return {
      key: config.key,
      type: config.type,
      amount: config.amount,
      label: translate(config.labelKey),
      shortLabel: translate(config.shortLabelKey),
      description: translate(config.descriptionKey),
      emptyMessage: translate(config.emptyMessageKey),
   }
}

/**
 * Builds the selectable period options, marking the active one.
 *
 * @param activeKey - The currently active period key.
 * @returns The period option list.
 */
function buildHistorySummaryPeriodOptions(activeKey: HistorySummaryPeriodKey) {
   return (Object.keys(HISTORY_SUMMARY_PERIOD_CONFIG) as HistorySummaryPeriodKey[]).map(
      periodKey => {
         const option = getHistorySummaryPeriod(periodKey)
         return {
            key: option.key,
            shortLabel: option.shortLabel,
            label: option.label,
            active: option.key === activeKey,
         }
      },
   )
}

/**
 * Returns the epoch-ms cutoff for a period (days or months back).
 *
 * @param period - The resolved period descriptor.
 * @returns The cutoff timestamp.
 */
function getHistorySummaryCutoff(period: ReturnType<typeof getHistorySummaryPeriod>) {
   if (period.type === 'days') {
      const days = Number.isFinite(period.amount) ? period.amount : 7
      return getDaysCutoff(days)
   }
   const months = Number.isFinite(period.amount) ? period.amount : 1
   return getMonthCutoff(months)
}

/**
 * Applies a history-summary update to the store, logging failures.
 *
 * @param detail - The summary event detail.
 */
function emitHistorySummary(detail: HistorySummaryEventDetail) {
   try {
      applyHistorySummaryUpdate(detail)
   } catch (error) {
      console.error('Failed to emit history summary update', error)
   }
}

/**
 * Computes and applies the history time-block ranges for a period.
 *
 * @param periodKey - The period key.
 * @param periodLabel - The localized period label.
 * @param entries - The fast entries to derive ranges from.
 */
function dispatchHistoryTimeBlocks(
   periodKey: string,
   periodLabel: string,
   entries: Array<FastEntry | null | undefined>,
) {
   try {
      const { ranges, currentRange } = collectTimeBlockRanges(entries)
      applyHistoryTimeBlocksUpdate({
         periodKey,
         periodLabel,
         ranges,
         currentFastRange:
            currentRange ? { start: currentRange.start, end: currentRange.end } : null,
         emittedAt: Date.now(),
      })
   } catch (error) {
      console.error('Failed to dispatch history time blocks', error)
   }
}

/**
 * Defers a history time-blocks dispatch to the next tick.
 *
 * @param periodKey - The period key.
 * @param periodLabel - The localized period label.
 * @param entries - The fast entries to derive ranges from.
 */
function scheduleHistoryTimeBlocks(
   periodKey: string,
   periodLabel: string,
   entries: Array<FastEntry | null | undefined>,
) {
   setTimeout(() => {
      dispatchHistoryTimeBlocks(periodKey, periodLabel, entries)
   }, 0)
}

/**
 * Sums grams of fat burned across fasts overlapping a time window.
 *
 * @param sources - The fast entries to consider.
 * @param cutoff - The window start in epoch ms.
 * @param windowEnd - The window end in epoch ms.
 * @returns Totals and counts of overlapping/counted fasts.
 */
function computeFatGramsForWindow(
   sources: FastEntry[],
   cutoff: number,
   windowEnd: number,
) {
   let totalGrams = 0
   let countedFasts = 0
   let overlappingFasts = 0

   sources.forEach(fast => {
      if (!fast || typeof fast.endTime !== 'number') {
         return
      }
      if (fast.endTime < cutoff) {
         return
      }

      const endTime = fast.endTime
      const durationMs = getFastEntryDurationMs(fast)
      const fallbackDuration = Number.isFinite(durationMs) ? durationMs : 0
      const startTime =
         typeof fast.startTime === 'number' && Number.isFinite(fast.startTime) ?
            fast.startTime
         :  endTime - fallbackDuration

      if (!Number.isFinite(startTime)) {
         return
      }

      const overlapStart = Math.max(startTime, cutoff)
      const overlapEnd = Math.min(endTime, windowEnd)
      if (overlapEnd <= overlapStart) {
         return
      }
      overlappingFasts += 1

      const offsetStartSeconds = Math.max(0, (overlapStart - startTime) / 1000)
      const offsetEndSeconds = Math.max(0, (overlapEnd - startTime) / 1000)
      if (offsetEndSeconds <= offsetStartSeconds) {
         return
      }

      const gramsAtEnd = getCumulativeFatBurned(offsetEndSeconds, DAILY_KCAL_DEFAULT, {
         skipCache: true,
      })
      const gramsAtStart = getCumulativeFatBurned(
         offsetStartSeconds,
         DAILY_KCAL_DEFAULT,
         {
            skipCache: true,
         },
      )

      const grams = gramsAtEnd - gramsAtStart
      if (!Number.isFinite(grams)) {
         return
      }
      const effectiveGrams = Math.max(0, grams)

      totalGrams += effectiveGrams
      if (effectiveGrams > 0) {
         countedFasts += 1
      }
   })

   return { totalGrams, countedFasts, overlappingFasts }
}

/**
 * Builds the summary event detail for local fast data.
 *
 * @param activePeriod - The selected period key.
 * @param sources - The fast entries (current fast first).
 * @returns The event detail.
 */
function buildLocalSummaryDetail(
   activePeriod: HistorySummaryPeriodKey,
   sources: FastEntry[],
): HistorySummaryEventDetail {
   const selectedPeriod = getHistorySummaryPeriod(activePeriod)
   const periodOptions = buildHistorySummaryPeriodOptions(selectedPeriod.key)
   const emittedAt = Date.now()
   const { totalGrams, countedFasts, overlappingFasts } = computeFatGramsForWindow(
      sources,
      getHistorySummaryCutoff(selectedPeriod),
      emittedAt,
   )
   if (sources.length === 0) {
      return {
         state: 'empty',
         label: 'History',
         emptyMessage: 'No fasts recorded yet.',
         periodOptions: [],
         source: 'local',
         emittedAt,
      }
   }
   if (overlappingFasts === 0) {
      return {
         state: 'no-fasts',
         label: selectedPeriod.label,
         emptyMessage: selectedPeriod.emptyMessage,
         periodOptions,
         source: 'local',
         emittedAt,
      }
   }
   return {
      state: 'summary',
      label: selectedPeriod.label,
      description: selectedPeriod.description,
      badgeCount: Math.max(countedFasts, overlappingFasts),
      formattedTotal: formatFatGrams(totalGrams),
      totalGrams,
      periodOptions,
      source: 'local',
      emittedAt,
   }
}

/**
 * Builds the summary event detail from server-provided totals.
 *
 * @param summaryData - The remote summary data (with a non-zero fast count).
 * @returns The event detail.
 */
function buildRemoteSummaryDetail(
   summaryData: HistorySummaryData,
): HistorySummaryEventDetail {
   const selectedPeriod = getHistorySummaryPeriod(summaryData.period)
   const totalGrams =
      Number.isFinite(summaryData.totalGrams) ? Math.max(0, summaryData.totalGrams) : 0
   return {
      state: 'summary',
      label: selectedPeriod.label,
      description: selectedPeriod.description,
      badgeCount: summaryData.fastCount,
      formattedTotal: formatFatGrams(summaryData.totalGrams || 0),
      totalGrams,
      periodOptions: buildHistorySummaryPeriodOptions(selectedPeriod.key),
      source: 'remote',
      emittedAt: Date.now(),
   }
}

/**
 * Creates the history-summary controller that renders totals from local or remote data.
 *
 * @param deps - Injected data sources and remote-fetch callbacks.
 * @returns The controller (update, selectPeriod, and helpers).
 */
export function createHistorySummaryController(deps: HistorySummaryControllerDeps) {
   let activePeriod: HistorySummaryPeriodKey = 'month'
   let freezeUntil = 0
   const remoteSummaryCache = new Map<
      HistorySummaryPeriodKey,
      { data: HistorySummaryData; ts: number }
   >()

   /**
    * Returns the fast entries to summarize, including the in-progress fast snapshot.
    *
    * @returns The combined fast entries.
    */
   function getSummarySources() {
      const currentSnapshot = deps.getCurrentFastSnapshot()
      const fastHistory = deps.getFastHistory()
      return currentSnapshot ? [currentSnapshot, ...fastHistory] : [...fastHistory]
   }

   /**
    * Computes and emits the history summary from local fast data.
    */
   function renderFromLocal() {
      const sources = getSummarySources()
      const detail = buildLocalSummaryDetail(activePeriod, sources)
      emitHistorySummary(detail)
      scheduleHistoryTimeBlocks(
         getHistorySummaryPeriod(activePeriod).key,
         detail.label,
         sources,
      )
   }

   /**
    * Emits the history summary from server-provided totals, falling back to local when empty.
    *
    * @param summaryData - The remote summary data.
    */
   function renderFromRemote(summaryData: HistorySummaryData) {
      if (!summaryData.fastCount) {
         renderFromLocal()
         return
      }
      const detail = buildRemoteSummaryDetail(summaryData)
      emitHistorySummary(detail)
      scheduleHistoryTimeBlocks(
         getHistorySummaryPeriod(summaryData.period).key,
         detail.label,
         getSummarySources(),
      )
   }

   /**
    * Fetches the remote summary for the active period, using the cache unless forced.
    *
    * @param force - Whether to bypass the cache.
    * @returns The summary, or null when unavailable.
    */
   async function loadRemoteSummary(force: boolean) {
      const cacheEntry = remoteSummaryCache.get(activePeriod)
      if (
         cacheEntry &&
         !force &&
         Date.now() - cacheEntry.ts < REMOTE_SUMMARY_CACHE_TTL_MS
      ) {
         return cacheEntry.data
      }
      let summary = null
      try {
         summary = await deps.fetchRemoteSummary(activePeriod)
      } catch (error) {
         console.error('Failed to fetch remote history summary', error)
      }
      if (!summary) {
         return null
      }
      const normalized: HistorySummaryData = {
         period:
            summary.period !== null && isHistorySummaryPeriod(summary.period) ?
               summary.period
            :  activePeriod,
         totalGrams: summary.totalGrams,
         fastCount: summary.fastCount,
      }
      remoteSummaryCache.set(activePeriod, { data: normalized, ts: Date.now() })
      return normalized
   }

   /**
    * Refreshes the summary, preferring cached/remote data and falling back to local.
    *
    * @param options - Set `force` to bypass the freeze window and cache.
    */
   async function update(options: { force?: boolean } = {}) {
      const force = options.force === true
      if (!force && freezeUntil > Date.now()) {
         return
      }
      const summary = deps.canFetchRemote() ? await loadRemoteSummary(force) : null
      if (summary) {
         renderFromRemote(summary)
      } else {
         renderFromLocal()
      }
   }

   /**
    * Switches the active summary period and refreshes.
    *
    * @param periodKey - The period key to activate.
    */
   function selectPeriod(periodKey: unknown) {
      if (
         typeof periodKey !== 'string' ||
         !periodKey ||
         periodKey === activePeriod ||
         !isHistorySummaryPeriod(periodKey)
      ) {
         return
      }
      activePeriod = periodKey
      void update({ force: true })
   }

   return {
      update,
      selectPeriod,
      /**
       * Returns the currently active summary period.
       *
       * @returns The active period key.
       */
      getPeriod: () => activePeriod,
      /**
       * Clears the cached remote summaries.
       */
      clearCache: () => remoteSummaryCache.clear(),
      /**
       * Suppresses summary re-renders for a duration.
       *
       * @param durationMs - How long to freeze, in milliseconds.
       */
      freezeFor: (durationMs: number) => {
         freezeUntil = Date.now() + durationMs
      },
   }
}
